import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Draw a divider above this item. */
  sep?: boolean;
}

interface Props {
  items: MenuItem[];
  /** Shown as the sheet heading on phones; also the button's accessible name. */
  title?: string;
}

const MENU_W = 200;
const SHEET_QUERY = "(max-width: 720px)";

// One "⋯" button per table row that opens its actions in a popover (desktop)
// or a bottom sheet (phones). Rendered through a portal so the table's
// horizontal-scroll container can't clip it.
export function ActionMenu({ items, title }: Props) {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const isSheet = window.matchMedia(SHEET_QUERY).matches;
    setSheet(isSheet);
    if (isSheet || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const est = items.length * 34 + 12;
    const up = r.bottom + est + 8 > window.innerHeight && r.top > est;
    setPos({
      top: up ? r.top - 4 : r.bottom + 4,
      left: Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8)),
      up,
    });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    // The desktop popover is position: fixed, so it would drift away from its
    // row on scroll; the phone sheet is anchored to the viewport and stays.
    if (!sheet) window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open, sheet]);

  const list = (
    <>
      {sheet && title && <div className="title">{title}</div>}
      {items.map((it, i) => (
        <div key={i}>
          {it.sep && i > 0 && <div className="sep" />}
          <button
            type="button"
            className={it.danger ? "danger" : ""}
            disabled={it.disabled}
            onClick={() => {
              setOpen(false);
              it.onClick();
            }}
          >
            {it.label}
          </button>
        </div>
      ))}
    </>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="secondary icon"
        aria-label={title ? `Actions: ${title}` : "Actions"}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
        onClick={() => setOpen((o) => !o)}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {open &&
        createPortal(
          <>
            <div
              className={`menu-backdrop ${sheet ? "sheet" : ""}`}
              onClick={() => setOpen(false)}
            />
            {sheet ? (
              <div className="menu sheet" role="menu">
                {list}
              </div>
            ) : (
              pos && (
                <div
                  className="menu"
                  role="menu"
                  style={{
                    top: pos.top,
                    left: pos.left,
                    width: MENU_W,
                    transform: pos.up ? "translateY(-100%)" : undefined,
                  }}
                >
                  {list}
                </div>
              )
            )}
          </>,
          document.body,
        )}
    </>
  );
}
